<template>
  <div>
    <Header title="Gestion des fonctions" />
    <div class="fonctions-view">
      <div class="container">
        <!-- En-tête avec bouton retour -->
        <div class="view-header">
          <router-link to="/parametres" class="back-button">
            <span class="material-icons">arrow_back</span>
            Retour aux paramètres
          </router-link>
          <h1>Gestion des fonctions</h1>
          <div class="header-actions">
            <button class="btn-new-fonction" @click="openModal()">
              <span class="material-icons">add_circle</span>
              Nouvelle fonction
            </button>
          </div>
        </div>
        
        <div class="row">
          <!-- Section de gauche : Liste des fonctions -->
          <div class="col-md-5 col-lg-4">
            <div class="card">
              <div class="card-header">
                <h5>Liste des fonctions</h5>
              </div>
              <div class="card-body">
                <div class="search-box">
                  <span class="material-icons search-icon">search</span>
                  <input 
                    type="text" 
                    v-model="searchTerm" 
                    placeholder="Rechercher une fonction..." 
                    class="search-input"
                  />
                </div>
                
                <div class="fonction-list" v-if="!loading">
                  <div 
                    v-for="fonction in filteredFonctions" 
                    :key="fonction.id"
                    class="fonction-item"
                    :class="{ 'selected': selectedFonction && selectedFonction.id === fonction.id, 'inactive': !fonction.active }"
                    @click="selectFonction(fonction)"
                  >
                    <div class="fonction-info">
                      <div class="fonction-name">{{ fonction.libelle }}</div>
                      <div class="fonction-code">{{ fonction.code }}</div>
                    </div>
                    <div class="fonction-actions">
                      <button @click.stop="openModal(fonction)" class="action-btn">
                        <span class="material-icons">edit</span>
                      </button>
                    </div>
                  </div>
                  
                  <div v-if="filteredFonctions.length === 0" class="empty-state">
                    <span class="material-icons empty-icon">search_off</span>
                    <p>Aucune fonction trouvée</p>
                  </div>
                </div>
                
                <div v-else class="loading-state">
                  <div class="spinner"></div>
                  <p>Chargement des fonctions...</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Section de droite : Détails et agents associés -->
          <div class="col-md-7 col-lg-8">
            <div class="card" v-if="selectedFonction">
              <div class="card-header">
                <div class="fonction-header">
                  <h5>{{ selectedFonction.libelle }}</h5>
                  <div class="fonction-badge" :class="{ 'active': selectedFonction.active, 'inactive': !selectedFonction.active }">
                    {{ selectedFonction.active ? 'Active' : 'Inactive' }}
                  </div>
                </div>
                <div class="fonction-actions">
                  <button @click="openModal(selectedFonction)" class="btn btn-outline-primary btn-sm">
                    <span class="material-icons">edit</span> Modifier
                  </button>
                  <button @click="openConfirmModal(selectedFonction)" class="btn btn-outline-danger btn-sm">
                    <span class="material-icons">delete</span> Supprimer
                  </button>
                </div>
              </div>
              
              <div class="card-body">
                <div class="fonction-details">
                  <div class="detail-item">
                    <span class="material-icons detail-icon">code</span>
                    <div>
                      <div class="detail-label">Code</div>
                      <div class="detail-value">{{ selectedFonction.code }}</div>
                    </div>
                  </div>
                  
                  <div class="detail-item" v-if="selectedFonction.niveau_competence">
                    <span class="material-icons detail-icon">trending_up</span>
                    <div>
                      <div class="detail-label">Niveau de compétence</div>
                      <div class="detail-value">
                        <div class="competence-level">
                          <div 
                            class="competence-bar" 
                            :style="{width: `${(selectedFonction.niveau_competence / 10) * 100}%`}"
                          ></div>
                        </div>
                        <div class="competence-value">{{ selectedFonction.niveau_competence }}/10</div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="detail-item" v-if="selectedFonction.description">
                    <span class="material-icons detail-icon">description</span>
                    <div>
                      <div class="detail-label">Description</div>
                      <div class="detail-value description">{{ selectedFonction.description }}</div>
                    </div>
                  </div>
                </div>
                
                <h5 class="agents-title">Agents assignés à cette fonction</h5>
                <div v-if="loadingAgents" class="loading-state">
                  <div class="spinner"></div>
                  <p>Chargement des agents...</p>
                </div>
                
                <div v-else>
                  <div class="agents-list" v-if="agents.length > 0">
                    <div class="agent-item" v-for="agent in agents" :key="agent.id" @click="goToAgentDetail(agent.id)">
                      <div class="agent-avatar">
                        <img v-if="agent.photo" :src="agent.photo" alt="Photo" />
                        <span v-else class="material-icons avatar-placeholder">person</span>
                      </div>
                      <div class="agent-info">
                        <div class="agent-name">{{ agent.nom }} {{ agent.prenom }}</div>
                        <div class="agent-matricule">{{ agent.matricule }}</div>
                      </div>
                      <span class="material-icons view-icon">chevron_right</span>
                    </div>
                  </div>
                  
                  <div v-else class="empty-state">
                    <span class="material-icons empty-icon">group_off</span>
                    <p>Aucun agent assigné à cette fonction</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else class="empty-selection">
              <span class="material-icons empty-icon">touch_app</span>
              <p>Sélectionnez une fonction pour voir ses détails et les agents associés</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal pour ajouter/modifier une fonction -->
    <dialog ref="fonctionModal" class="pays-modal fonction-modal">
      <div class="modal-header">
        <h3>{{ fonctionToEdit ? 'Modifier une fonction' : 'Ajouter une fonction' }}</h3>
        <button class="btn-close" @click="closeModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <!-- Formulaire intégré directement -->
        <div v-if="showModal" class="fonction-form-direct">
          <!-- Affichage des erreurs -->
          <div v-if="formError" class="alert alert-danger">{{ formError }}</div>
          
          <form @submit.prevent="saveFonction" class="form-container">
            <!-- Code de la fonction -->
            <div class="form-group">
              <label for="code">Code de la fonction *</label>
              <div class="input-with-icon">
                <span class="material-icons input-icon">code</span>
                <input 
                  type="text" 
                  id="code" 
                  v-model="formData.code" 
                  class="form-control" 
                  required
                  :disabled="fonctionToEdit"
                  maxlength="20"
                  placeholder="Ex: DIR-GEN"
                >
              </div>
            </div>
            
            <!-- Libellé de la fonction -->
            <div class="form-group">
              <label for="libelle">Libellé *</label>
              <div class="input-with-icon">
                <span class="material-icons input-icon">title</span>
                <input 
                  type="text" 
                  id="libelle" 
                  v-model="formData.libelle" 
                  class="form-control" 
                  required
                  maxlength="100"
                  placeholder="Ex: Directeur Général"
                >
              </div>
            </div>
            
            <!-- Niveau de compétence -->
            <div class="form-group">
              <label for="niveau_competence">Niveau de compétence (1-10)</label>
              <div class="input-with-icon">
                <span class="material-icons input-icon">trending_up</span>
                <input 
                  type="number" 
                  id="niveau_competence" 
                  v-model.number="formData.niveau_competence" 
                  class="form-control" 
                  min="1"
                  max="10"
                  placeholder="Ex: 8"
                >
              </div>
            </div>
            
            <!-- Description de la fonction -->
            <div class="form-group">
              <label for="description">Description</label>
              <div class="input-with-icon textarea-icon">
                <span class="material-icons input-icon">description</span>
                <textarea 
                  id="description" 
                  v-model="formData.description" 
                  class="form-control" 
                  rows="3"
                  placeholder="Description détaillée de la fonction..."
                ></textarea>
              </div>
            </div>
            
            <!-- Statut actif/inactif -->
            <!--<div class="form-group switch-control">
              <label class="switch-label">
                <span class="switch-label-text">Fonction active</span>
                <div class="switch">
                  <input 
                    type="checkbox" 
                    id="active" 
                    v-model="formData.active" 
                    class="switch-input"
                  >
                  <label for="active" class="switch-label">
                    <span class="switch-button"></span>
                  </label>
                </div>
              </label>
            </div>-->
            
            <!-- Boutons d'action -->
            <div class="form-actions">
              <button type="button" @click="closeModal" class="btn btn-secondary">
                <span class="material-icons">cancel</span> Annuler
              </button>
              <button type="submit" class="btn btn-primary" :disabled="savingFonction">
                <span class="material-icons">{{ fonctionToEdit ? 'save' : 'add_circle' }}</span>
                {{ fonctionToEdit ? 'Mettre à jour' : 'Ajouter' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </dialog>
    
    <!-- Modal de confirmation de suppression -->
    <dialog ref="confirmDeleteModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button class="btn-close" @click="closeConfirmModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir supprimer la fonction "{{ fonctionToDelete?.libelle }}" ?</p>
        <p class="warning">Cette action est irréversible et peut affecter tous les agents associés à cette fonction.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmModal">Annuler</button>
        <button class="btn btn-danger" @click="deleteFonction">Supprimer</button>
      </div>
    </dialog>
    
    <!-- Composant de notification -->
    <NotificationToast 
      v-if="notification.show"
      :type="notification.type"
      :title="notification.title"
      :message="notification.message"
      @close="notification.show = false"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '../services/api';
import Header from '../components/Header.vue';
import NotificationToast from '../components/NotificationToast.vue';

// État local
const router = useRouter();
const showModal = ref(false);
const confirmDeleteModal = ref(null);
const fonctionModal = ref(null);
const fonctions = ref([]);
const fonctionToEdit = ref(null);
const fonctionToDelete = ref(null);
const loading = ref(true);
const savingFonction = ref(false);
const showSuccessToast = ref(false);
const toastMessage = ref('');
const formError = ref('');
const searchTerm = ref('');
const selectedFonction = ref(null);
const agents = ref([]);
const loadingAgents = ref(false);

// Données du formulaire
const formData = reactive({
  code: '',
  libelle: '',
  niveau_competence: 1,
  description: '',
  active: true
});

// Réinitialiser le formulaire
const resetForm = () => {
  formData.code = '';
  formData.libelle = '';
  formData.niveau_competence = 1;
  formData.description = '';
  formData.active = true;
  formError.value = '';
};

// Remplir le formulaire avec les données d'une fonction existante
const populateForm = (fonction) => {
  formData.code = fonction.code;
  formData.libelle = fonction.libelle;
  formData.niveau_competence = fonction.niveau_competence || 1;
  formData.description = fonction.description || '';
  formData.active = fonction.active;
};

// Sauvegarder une fonction (ajout ou modification)
const saveFonction = async () => {
  try {
    savingFonction.value = true;
    formError.value = '';
    
    const fonctionData = {
      code: formData.code,
      libelle: formData.libelle,
      niveau_competence: formData.niveau_competence || 1,
      description: formData.description,
      active: formData.active
    };
    
    let response;
    
    if (fonctionToEdit.value) {
      // Mode édition
      response = await api.put(`/api/parametres/fonctions/${fonctionToEdit.value.id}`, fonctionData);
      toastMessage.value = 'Fonction mise à jour avec succès';
    } else {
      // Mode création
      response = await api.post('/api/parametres/fonctions', fonctionData);
      toastMessage.value = 'Fonction ajoutée avec succès';
    }
    
    showSuccessToast.value = true;
    closeModal();
    loadFonctions();
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement de la fonction:', error);
    formError.value = error.response?.data?.message || 'Erreur lors de l\'enregistrement de la fonction';
  } finally {
    savingFonction.value = false;
  }
};

// État de la notification
const notification = ref({
  show: false,
  type: 'success',
  title: '',
  message: ''
});

// Filtrage des fonctions
const filteredFonctions = computed(() => {
  let result;
  
  if (!searchTerm.value.trim()) {
    result = [...fonctions.value];
  } else {
    const search = searchTerm.value.trim().toLowerCase();
    result = fonctions.value.filter(fonction => 
      fonction.libelle.toLowerCase().includes(search) || 
      fonction.code.toLowerCase().includes(search)
    );
  }
  
  // Tri par niveau de compétence croissant
  return result.sort((a, b) => {
    // Convertir en nombre pour assurer un tri numérique correct
    const niveauA = Number(a.niveau_competence) || 0;
    const niveauB = Number(b.niveau_competence) || 0;
    return niveauA - niveauB;
  });
});

// Charger la liste des fonctions
async function loadFonctions() {
  loading.value = true;
  
  try {
    const response = await api.getFonctions();
    //console.log('Réponse API fonctions:', response);
    
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      fonctions.value = response.data.data.map(fonction => ({
        ...fonction,
        active: fonction.active === 1 || fonction.active === true
      }));
      
      // Si une fonction était sélectionnée, mettre à jour sa version
      if (selectedFonction.value) {
        const updated = fonctions.value.find(f => f.id === selectedFonction.value.id);
        if (updated) {
          selectedFonction.value = updated;
        }
      }
    }
  } catch (error) {
    console.error('Erreur lors du chargement des fonctions:', error);
    showNotification('error', 'Erreur', 'Impossible de charger la liste des fonctions');
  } finally {
    loading.value = false;
  }
}

// Charger les agents assignés à une fonction
async function loadAgents(fonctionId) {
  if (!fonctionId) return;
  
  loadingAgents.value = true;
  agents.value = [];
  
  try {
    const response = await api.getAgentsByFonction(fonctionId);
    if (response.data && Array.isArray(response.data)) {
      agents.value = response.data;
    }
  } catch (error) {
    console.error('Erreur lors du chargement des agents:', error);
  } finally {
    loadingAgents.value = false;
  }
}

// Sélectionner une fonction et charger ses agents
function selectFonction(fonction) {
  selectedFonction.value = fonction;
  loadAgents(fonction.id);
}

// Naviguer vers la fiche de détail d'un agent
function goToAgentDetail(agentId) {
  router.push(`/personnel/${agentId}`);
}

// Ouvrir la modale pour ajouter ou modifier une fonction
const openModal = (fonction = null) => {
  resetForm();
  fonctionToEdit.value = fonction;
  
  if (fonction) {
    // Mode édition - populer le formulaire
    populateForm(fonction);
  }
  
  showModal.value = true;
  nextTick(() => {
    fonctionModal.value.showModal();
  });
};

// Fermer la modale
const closeModal = () => {
  if (fonctionModal.value) {
    fonctionModal.value.close();
  }
  showModal.value = false;
  fonctionToEdit.value = null;
  resetForm();
};

// Gérer le succès d'une opération
function handleSuccess(result) {
  if (result?.message) {
    showNotification('success', 'Succès', result.message);
  }
}

// Ouvrir la modale de confirmation de suppression
function openConfirmModal(fonction) {
  fonctionToDelete.value = fonction;
  nextTick(() => {
    confirmDeleteModal.value.showModal();
  });
}

// Fermer la modale de confirmation
function closeConfirmModal() {
  confirmDeleteModal.value.close();
  fonctionToDelete.value = null;
}

// Supprimer une fonction
async function deleteFonction() {
  try {
    const response = await api.delete(`/api/parametres/fonctions/${fonctionToDelete.value.id}`);
    showNotification('success', 'Succès', 'Fonction supprimée avec succès');
    closeConfirmModal();
    loadFonctions();
    
    // Si la fonction supprimée était sélectionnée, on désélectionne
    if (selectedFonction.value && selectedFonction.value.id === fonctionToDelete.value.id) {
      selectedFonction.value = null;
    }
  } catch (error) {
    console.error('Erreur lors de la suppression de la fonction:', error);
    showNotification('error', 'Erreur', error.response?.data?.message || 'Erreur lors de la suppression');
  }
}

// Afficher une notification
function showNotification(type, title, message) {
  notification.value = {
    show: true,
    type,
    title,
    message
  };
  
  // Masquer automatiquement après 5 secondes
  setTimeout(() => {
    notification.value.show = false;
  }, 5000);
}

// Observateur pour recharger les agents si la fonction sélectionnée change
watch(
  () => selectedFonction.value?.id,
  (newId) => {
    if (newId) {
      loadAgents(newId);
    }
  }
);

// Charger les données au montage du composant
onMounted(() => {
  loadFonctions();
});
</script>

<style scoped>
.fonctions-view {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.back-button {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #666;
  font-weight: 500;
  transition: color 0.2s;
}

.back-button:hover {
  color: var(--turquoise-primary);
}

.back-button .material-icons {
  margin-right: 5px;
  font-size: 20px;
}

.view-header h1 {
  margin: 0;
  font-size: 1.8rem;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.btn-new-fonction {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: linear-gradient(135deg, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 0 4px 10px rgba(48, 199, 181, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;
}

.btn-new-fonction:hover {
  background: linear-gradient(135deg, var(--turquoise-very-dark), var(--turquoise-deep));
  box-shadow: 0 6px 12px rgba(21, 87, 81, 0.4);
  transform: translateY(-2px);
}

.btn-new-fonction:active {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(21, 87, 81, 0.4);
}

.btn-new-fonction .material-icons {
  margin-right: 8px;
  font-size: 20px;
}

.container {
  max-width: 100%;
}

.card {
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  background-color: #fff;
  border: none;
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background-color: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.card-header h5 {
  margin: 0;
  font-weight: 600;
  color: #333;
}

.card-body {
  padding: 20px;
}

/* Search */
.search-box {
  position: relative;
  margin-bottom: 15px;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input {
  width: 100%;
  padding: 10px 10px 10px 40px;
  border: 1px solid #ced4da;
  border-radius: 5px;
  font-size: 14px;
}

/* Liste des fonctions */
.fonction-list {
  max-height: 500px;
  overflow-y: auto;
}

.fonction-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: all 0.2s;
  border-left: 3px solid transparent;
}

.fonction-item:last-child {
  border-bottom: none;
}

.fonction-item:hover {
  background-color: #f8f9fa;
  border-left-color: var(--turquoise-primary);
}

.fonction-item.selected {
  background-color: var(--turquoise-pale);
  border-left-color: var(--turquoise-primary);
}

.fonction-item.inactive {
  opacity: 0.7;
}

.fonction-info {
  flex: 1;
}

.fonction-name {
  font-weight: 500;
  margin-bottom: 2px;
}

.fonction-code {
  font-size: 12px;
  color: #6c757d;
}

.fonction-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.fonction-item:hover .fonction-actions {
  opacity: 1;
}

.action-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 5px;
  transition: color 0.2s;
}

.action-btn:hover {
  color: var(--turquoise-primary);
}

/* Header de la fonction */
.fonction-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fonction-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.fonction-badge.active {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
}

.fonction-badge.inactive {
  background-color: #ffebee;
  color: var(--coral-soft);
}

/* Détails de la fonction */
.fonction-details {
  margin-bottom: 30px;
}

.detail-item {
  display: flex;
  margin-bottom: 20px;
  align-items: flex-start;
}

.detail-icon {
  margin-right: 15px;
  color: var(--turquoise-primary);
  background-color: var(--turquoise-pale);
  padding: 8px;
  border-radius: 50%;
}

.detail-label {
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 3px;
}

.detail-value {
  font-weight: 500;
}

.detail-value.description {
  white-space: pre-line;
}

/* Niveau de compétence */
.competence-level {
  width: 200px;
  height: 10px;
  background-color: #e0e0e0;
  border-radius: 5px;
  margin-bottom: 5px;
  overflow: hidden;
}

.competence-bar {
  height: 100%;
  background-color: var(--turquoise-primary);
  border-radius: 5px;
}

.competence-value {
  font-size: 12px;
  color: #6c757d;
}

/* Liste des agents */
.agents-title {
  margin-top: 30px;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 600;
}

.agents-list {
  max-height: 300px;
  overflow-y: auto;
  border-top: 1px solid #eee;
}

.agent-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s;
}

.agent-item:hover {
  background-color: #f8f9fa;
}

.agent-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #e0e0e0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
}

.avatar-placeholder {
  color: #9e9e9e;
  font-size: 24px;
}

.agent-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.agent-info {
  flex: 1;
}

.agent-name {
  font-weight: 500;
  margin-bottom: 2px;
}

.agent-matricule {
  font-size: 12px;
  color: #6c757d;
}

.view-icon {
  color: #9e9e9e;
}

/* États vides et chargement */
.empty-state, .loading-state, .empty-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  color: #9e9e9e;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(48, 199, 181, 0.2);
  border-radius: 50%;
  border-top-color: var(--turquoise-primary);
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-selection {
  height: 100%;
  min-height: 300px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Modal styles */
.pays-modal.fonction-modal {
  width: 90%;
  max-width: 600px;
  border: none;
  border-radius: 10px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  padding: 0;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}

.pays-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.pays-modal .modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: white;
  display: flex;
  align-items: center;
}

.pays-modal .btn-close {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: white;
}

.pays-modal .btn-close:hover {
  color: rgba(255, 255, 255, 0.8);
}

.pays-modal .modal-body {
  padding: 0;
  background-color: #ffffff;
}

/* Dialog backdrop */
::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

/* Styles du formulaire intégré */
.fonction-form-direct {
  background-color: #ffffff;
  padding: 20px;
  border-radius: 0 0 8px 8px;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
  margin-top: 6px;
}

.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--turquoise-primary);
  font-size: 20px;
  z-index: 1;
}

.textarea-icon .input-icon {
  top: 16px;
  transform: none;
}

.form-control {
  padding: 12px 12px 12px 40px;
  border-radius: 5px;
  border: 2px solid #d1d9e6;
  background-color: #ffffff;
  width: 100%;
  font-size: 14px;
  color: #333333;
  transition: all 0.2s;
}

.form-control:focus {
  border-color: var(--turquoise-primary);
  box-shadow: 0 0 0 2px rgba(48, 199, 181, 0.2);
  outline: none;
}

textarea.form-control {
  padding-top: 35px;
  resize: vertical;
  min-height: 100px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

/* Switch styles */
.switch-control {
  margin-top: 30px;
}

.switch-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.switch-label-text {
  font-weight: 500;
}

.switch {
  position: relative;
  width: 50px;
  height: 24px;
}

.switch-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-label {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.switch-button {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

.switch-input:checked + .switch-label {
  background-color: var(--turquoise-primary);
}

.switch-input:checked + .switch-label .switch-button {
  transform: translateX(26px);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
  gap: 10px;
}

.btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn .material-icons {
  margin-right: 8px;
  font-size: 18px;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
  box-shadow: 0 2px 5px rgba(48, 199, 181, 0.3);
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
  box-shadow: 0 4px 8px rgba(21, 87, 81, 0.4);
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #666;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.alert {
  padding: 12px 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-danger {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
}

/* Styles de la modale de confirmation de suppression */
.confirm-modal {
  border: none;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  padding: 0;
  max-width: 450px;
  width: 100%;
}

.confirm-modal .modal-header {
  background-color: #f8d7da;
  color: #721c24;
  border-bottom: 1px solid #f5c6cb;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px 8px 0 0;
}

.confirm-modal .modal-body {
  padding: 20px;
  background-color: #fff;
}

.confirm-modal .modal-footer {
  padding: 15px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #eee;
  background-color: #f9f9f9;
  border-radius: 0 0 8px 8px;
}

.warning {
  color: #856404;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  padding: 10px 15px;
  border-radius: 4px;
  margin-top: 10px;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
  box-shadow: 0 2px 5px rgba(220, 53, 69, 0.3);
}

.btn-danger:hover {
  background-color: #c82333;
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.4);
}

.btn-outline-danger {
  color: #dc3545;
  border: 1px solid #dc3545;
  background-color: transparent;
}

.btn-outline-danger:hover {
  color: white;
  background-color: #dc3545;
}

.fonction-actions {
  display: flex;
  gap: 8px;
}
</style>
